import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, Brain, FileText, Calendar, TrendingUp, 
  MessageCircle, ChevronRight, Sparkles, Heart, Dna,
  Bell, Check, Clock, Plus, Baby, Leaf
} from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usersAPI, checkinsAPI, genotypesAPI, remindersAPI } from '../lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

// Map journey stage to display text
const journeyStageText = {
  'preparing': 'Preparing for pregnancy',
  'trying': 'Trying to conceive',
  'ivf': 'Undergoing IVF',
  'pregnant': 'Pregnant',
  'postpartum': 'Postpartum'
};

// Map journey stage to colors
const journeyStageColors = {
  'preparing': 'bg-[#D4A5A5]/20 text-[#1A3C34]',
  'trying': 'bg-[#D4A5A5]/30 text-[#1A3C34]',
  'ivf': 'bg-purple-100 text-purple-800',
  'pregnant': 'bg-pink-100 text-pink-800',
  'postpartum': 'bg-green-100 text-green-800'
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [genotype, setGenotype] = useState(null);
  const [genotypeRecommendations, setGenotypeRecommendations] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, checkinRes, genotypeRes, remindersRes, recommendationsRes] = await Promise.all([
        usersAPI.getDashboardStats(),
        checkinsAPI.getToday(),
        genotypesAPI.getMyGenotype().catch(() => ({ data: { genotype: null } })),
        remindersAPI.getToday().catch(() => ({ data: { reminders: [] } })),
        genotypesAPI.getRecommendations().catch(() => ({ data: { recommendations: [], quick_tips: [] } }))
      ]);
      setStats(statsRes.data);
      setTodayCheckin(checkinRes.data);
      setGenotype(genotypeRes.data.genotype);
      setReminders(remindersRes.data.reminders || []);
      setGenotypeRecommendations(recommendationsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await remindersAPI.complete(reminderId);
      setReminders(prev => prev.map(r => 
        r.id === reminderId ? { ...r, is_completed: true } : r
      ));
      toast.success('Task completed!');
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const chartData = stats?.recent_checkins?.map(checkin => {
    const date = new Date(checkin.created_at).toLocaleDateString('en-US', { weekday: 'short' });
    const scores = {};
    checkin.responses?.forEach(r => {
      scores[r.category] = r.rating;
    });
    return { date, ...scores };
  }).reverse() || [];

  const profile = user?.profile;
  const journeyStage = profile?.journey_stage;
  
  // Calculate BMI if available
  let bmi = null;
  let bmiCategory = null;
  if (profile?.height_cm && profile?.weight_kg) {
    const heightM = profile.height_cm / 100;
    bmi = (profile.weight_kg / (heightM * heightM)).toFixed(1);
    if (bmi < 18.5) bmiCategory = { label: 'Underweight', color: 'text-blue-600' };
    else if (bmi < 25) bmiCategory = { label: 'Normal', color: 'text-green-600' };
    else if (bmi < 30) bmiCategory = { label: 'Overweight', color: 'text-yellow-600' };
    else bmiCategory = { label: 'Obese', color: 'text-red-600' };
  }
  
  // Calculate average wellness score
  const avgScores = stats?.average_scores || {};
  const scoreValues = Object.values(avgScores).filter(v => v > 0);
  const overallWellness = scoreValues.length > 0 
    ? (scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length).toFixed(1)
    : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingReminders = reminders.filter(r => !r.is_completed);
  const completedReminders = reminders.filter(r => r.is_completed);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-br from-[#D4A5A5]/20 to-[#1A3C34]/5 rounded-3xl p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl text-[#1A3C34] mb-1">
                Welcome back, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-[#1A3C34]/70 text-sm lg:text-base">
                {todayCheckin?.completed 
                  ? "You've completed your daily check-in. Keep up the great work!"
                  : "How are you feeling today? Track your wellness journey."}
              </p>
              {journeyStage && (
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${journeyStageColors[journeyStage] || 'bg-gray-100 text-gray-700'}`}>
                  {journeyStageText[journeyStage] || journeyStage}
                </span>
              )}
            </div>
            {!todayCheckin?.completed && (
              <Link to="/checkin">
                <Button className="bg-[#1A3C34] text-white hover:bg-[#152E28] rounded-full px-6" data-testid="dashboard-checkin-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> Daily Check-in
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Quick Stats Widget */}
        <div className="soft-card p-6 border border-[#D4A5A5]/20">
          <h2 className="font-heading text-lg text-[#1A3C34] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#D4A5A5]" /> Your Stats
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
              <p className="text-2xl font-heading text-[#1A3C34]">{stats?.streak_days || 0}</p>
              <p className="text-xs text-[#1A3C34]/60">Day Streak</p>
            </div>
            <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
              <p className="text-2xl font-heading text-[#1A3C34]">{overallWellness || 'N/A'}</p>
              <p className="text-xs text-[#1A3C34]/60">Wellness Score</p>
            </div>
            <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
              <p className="text-2xl font-heading text-[#1A3C34]">{bmi || 'N/A'}</p>
              <p className="text-xs text-[#1A3C34]/60">
                BMI {bmiCategory && <span className={bmiCategory.color}>({bmiCategory.label})</span>}
              </p>
            </div>
            <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
              <p className="text-2xl font-heading text-[#1A3C34]">{avgScores.mood?.toFixed(1) || 'N/A'}</p>
              <p className="text-xs text-[#1A3C34]/60">Avg Mood</p>
            </div>
            <div className="text-center p-4 bg-[#FAFAFA] rounded-xl">
              <p className="text-2xl font-heading text-[#1A3C34]">{avgScores.energy?.toFixed(1) || 'N/A'}</p>
              <p className="text-xs text-[#1A3C34]/60">Avg Energy</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Charts and Genotype */}
          <div className="lg:col-span-2 space-y-6">
            {/* Genotype Widget (only if user has genotype) */}
            {genotype && (
              <div className="soft-card p-6 border-l-4 border-[#1A3C34]" data-testid="genotype-widget">
                <div className="flex items-start gap-4">
                  {genotype.image_url && (
                    <img 
                      src={genotype.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${genotype.image_url}` : genotype.image_url}
                      alt={genotype.name}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Dna className="w-5 h-5 text-[#1A3C34]" />
                      <span className="text-xs text-[#1A3C34] font-medium uppercase tracking-wider">Your Genotype</span>
                    </div>
                    <h3 className="font-heading text-xl text-[#1A3C34] mb-1">{genotype.name}</h3>
                    {genotype.motto && (
                      <p className="text-sm text-[#1A3C34]/60 italic mb-2">&quot;{genotype.motto}&quot;</p>
                    )}
                    {genotype.greatest_strength && (
                      <p className="text-sm text-[#1A3C34]/70">
                        <span className="text-[#1A3C34]">+</span> {genotype.greatest_strength}
                      </p>
                    )}
                    <Link to={`/genotype/${genotype.id}`}>
                      <Button variant="ghost" size="sm" className="mt-3 text-[#1A3C34] hover:text-[#152E28] p-0" data-testid="genotype-learn-more">
                        Learn more <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Fertility Tips Card */}
            <div className="soft-card p-6 bg-gradient-to-br from-[#F2E8E8] to-white border border-[#D4A5A5]/20">
              <div className="flex items-center gap-2 mb-4">
                <Baby className="w-5 h-5 text-[#D4A5A5]" />
                <h2 className="font-heading text-lg text-[#1A3C34]">Fertility Tip</h2>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#D4A5A5]/10">
                <p className="text-sm text-[#1A3C34]/80 leading-relaxed">
                  {journeyStage === 'trying' || journeyStage === 'preparing' ? (
                    <>
                      <strong className="text-[#1A3C34]">Did you know?</strong> The choices you make today can influence not just your fertility, but your future baby&apos;s gene expression through epigenetics. Focus on whole foods rich in folate, omega-3s, and antioxidants.
                    </>
                  ) : journeyStage === 'ivf' ? (
                    <>
                      <strong className="text-[#1A3C34]">IVF Support:</strong> Stress management is key during your IVF journey. Consider gentle movement, meditation, and adequate sleep. Your body is doing incredible work right now.
                    </>
                  ) : journeyStage === 'pregnant' ? (
                    <>
                      <strong className="text-[#1A3C34]">Pregnancy nutrition:</strong> Choline is essential for your baby&apos;s brain development but often overlooked. Good sources include eggs, liver, and soybeans. Ask Dr. Erika for personalized recommendations.
                    </>
                  ) : journeyStage === 'postpartum' ? (
                    <>
                      <strong className="text-[#1A3C34]">Postpartum recovery:</strong> Your body has done something amazing. Prioritize protein for tissue repair, iron-rich foods to replenish stores, and omega-3s for mood support during this transition.
                    </>
                  ) : (
                    <>
                      <strong className="text-[#1A3C34]">Epigenetics insight:</strong> Your lifestyle choices today can influence gene expression for generations. Complete your onboarding to get personalized fertility guidance.
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Wellness Chart */}
            <div className="soft-card p-6 border border-[#D4A5A5]/20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg text-[#1A3C34]">Wellness Trends</h2>
                <Link to="/checkin" className="text-sm text-[#D4A5A5] hover:text-[#C89494] flex items-center gap-1">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {chartData.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <XAxis dataKey="date" stroke="#1A3C34" opacity={0.4} fontSize={11} />
                      <YAxis domain={[0, 5]} stroke="#1A3C34" opacity={0.4} fontSize={11} />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid rgba(212, 165, 165, 0.2)',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}
                      />
                      <Line type="monotone" dataKey="mood" stroke="#D4A5A5" strokeWidth={2} dot={{ fill: '#D4A5A5', r: 3 }} />
                      <Line type="monotone" dataKey="energy" stroke="#1A3C34" strokeWidth={2} dot={{ fill: '#1A3C34', r: 3 }} />
                      <Line type="monotone" dataKey="sleep" stroke="#7C9885" strokeWidth={2} dot={{ fill: '#7C9885', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-[#1A3C34]/50">
                  <div className="text-center">
                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Complete your first check-in to see trends</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-4 mt-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#D4A5A5]" />
                  <span className="text-[#1A3C34]/70">Mood</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#1A3C34]" />
                  <span className="text-[#1A3C34]/70">Energy</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#7C9885]" />
                  <span className="text-[#1A3C34]/70">Sleep</span>
                </div>
              </div>
            </div>

            {/* Genotype Recommendations Widget */}
            {genotypeRecommendations && (genotypeRecommendations.quick_tips?.length > 0 || genotypeRecommendations.recommendations?.length > 0) && (
              <div className="soft-card p-6 border border-[#D4A5A5]/20" data-testid="genotype-recommendations-widget">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg text-[#1A3C34] flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-[#1A3C34]" /> For Your Genotype
                  </h2>
                  {genotype && (
                    <Link to={`/genotype/${genotype.id}`} className="text-xs text-[#1A3C34] hover:text-[#152E28]">
                      View all →
                    </Link>
                  )}
                </div>

                {/* Quick Tips */}
                {genotypeRecommendations.quick_tips?.length > 0 && (
                  <div className="grid sm:grid-cols-3 gap-3 mb-4">
                    {genotypeRecommendations.quick_tips.map((tip, index) => (
                      <div 
                        key={index}
                        className={`p-3 rounded-xl ${
                          tip.type === 'strength' ? 'bg-[#1A3C34]/10' :
                          tip.type === 'challenge' ? 'bg-[#D4A5A5]/10' :
                          'bg-[#7C9885]/10'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {tip.type === 'strength' && <Heart className="w-4 h-4 text-[#1A3C34]" />}
                          {tip.type === 'challenge' && <Activity className="w-4 h-4 text-[#D4A5A5]" />}
                          {tip.type === 'health' && <Heart className="w-4 h-4 text-[#7C9885]" />}
                          <span className="text-xs font-medium text-[#1A3C34]">{tip.title}</span>
                        </div>
                        <p className="text-xs text-[#1A3C34]/70 line-clamp-2">{tip.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Document Recommendations */}
                {genotypeRecommendations.recommendations?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-[#1A3C34]/50 uppercase tracking-wider mb-2">Recommended Reading</p>
                    {genotypeRecommendations.recommendations.slice(0, 3).map((rec, index) => (
                      <div 
                        key={rec.id || index}
                        className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-xl hover:bg-[#D4A5A5]/5 transition-all cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#D4A5A5]/20 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-[#D4A5A5]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A3C34] truncate">{rec.title}</p>
                          <p className="text-xs text-[#1A3C34]/50">{rec.folder_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Reminders and Wellness Advisor */}
          <div className="space-y-6">
            {/* Daily Reminders Widget */}
            <div className="soft-card p-6 border border-[#D4A5A5]/20" data-testid="reminders-widget">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg text-[#1A3C34] flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#D4A5A5]" /> Today&apos;s Tasks
                </h2>
                <Link to="/reminders">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Plus className="w-4 h-4 text-[#1A3C34]/60" />
                  </Button>
                </Link>
              </div>

              {pendingReminders.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {pendingReminders.slice(0, 5).map(reminder => (
                    <div 
                      key={reminder.id}
                      className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-xl"
                    >
                      <button
                        onClick={() => handleCompleteReminder(reminder.id)}
                        className="w-6 h-6 rounded-full border-2 border-[#D4A5A5]/30 hover:border-[#D4A5A5] hover:bg-[#D4A5A5]/10 flex items-center justify-center transition-all"
                        data-testid={`complete-reminder-${reminder.id}`}
                      >
                        <Check className="w-3 h-3 text-transparent hover:text-[#D4A5A5]" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A3C34] truncate">{reminder.title}</p>
                        {reminder.time_of_day && (
                          <p className="text-xs text-[#1A3C34]/50 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {reminder.time_of_day}
                          </p>
                        )}
                      </div>
                      {reminder.created_by === 'ai' && (
                        <span className="px-2 py-0.5 bg-[#1A3C34]/10 text-[#1A3C34] rounded text-xs">AI</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-[#1A3C34]/50">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tasks for today</p>
                </div>
              )}

              {completedReminders.length > 0 && (
                <div className="border-t border-[#D4A5A5]/10 pt-3">
                  <p className="text-xs text-[#1A3C34]/50 mb-2">{completedReminders.length} completed today</p>
                  {completedReminders.slice(0, 2).map(reminder => (
                    <div key={reminder.id} className="flex items-center gap-2 text-sm text-[#1A3C34]/40 line-through mb-1">
                      <Check className="w-4 h-4 text-[#1A3C34]" />
                      <span className="truncate">{reminder.title}</span>
                    </div>
                  ))}
                </div>
              )}

              <Link to="/reminders" className="block mt-4">
                <Button variant="outline" size="sm" className="w-full text-[#1A3C34]/70 border-[#D4A5A5]/30">
                  Manage Reminders
                </Button>
              </Link>
            </div>

            {/* Fertility Advisor Quick Access */}
            <div className="soft-card p-6 bg-gradient-to-br from-[#D4A5A5]/10 to-[#F2E8E8] border border-[#D4A5A5]/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4A5A5] to-[#C89494] flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-lg text-[#1A3C34]">Dr. Erika</h2>
                  <p className="text-xs text-[#1A3C34]/60">Fertility & Epigenetics Expert</p>
                </div>
              </div>
              
              <p className="text-sm text-[#1A3C34]/70 mb-4">
                Get personalized fertility and nutrition advice anytime. Click the chat bubble in the bottom-right corner.
              </p>

              <div className="flex items-center justify-between p-3 bg-white/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#D4A5A5] flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm text-[#1A3C34]">Available 24/7</span>
                </div>
                <span className="text-xs text-[#1A3C34]/60 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Online
                </span>
              </div>
            </div>

            {/* Quick Links */}
            <div className="soft-card p-6 border border-[#D4A5A5]/20">
              <h2 className="font-heading text-lg text-[#1A3C34] mb-3">Quick Links</h2>
              <div className="space-y-2">
                <Link 
                  to="/reports"
                  className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-xl hover:bg-[#D4A5A5]/10 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-[#1A3C34]" />
                    <span className="text-sm text-[#1A3C34]">Health Reports</span>
                  </div>
                  <span className="text-xs text-[#1A3C34]/50">{stats?.reports_count || 0}</span>
                </Link>

                <Link 
                  to="/profile"
                  className="flex items-center justify-between p-3 bg-[#FAFAFA] rounded-xl hover:bg-[#D4A5A5]/10 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Activity className="w-4 h-4 text-[#D4A5A5]" />
                    <span className="text-sm text-[#1A3C34]">Update Profile</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#1A3C34]/30" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
